import { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  Activity,
  Award,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
  X,
} from "lucide-react";
import RulesService, { type Rule, type RuleEvaluationLogEntry } from "../../../_services/RulesService";
import EligibilityQueue, { type EligibilityQueueHandle } from "./EligibilityQueue";
import { predicateToString } from "./RuleSummary";

interface Props {
  show: boolean;
  onHide: () => void;
  ruleId: number | null;
  onEdit: (rule: Rule) => void;
}

const RuleDetail: React.FC<Props> = ({ show, onHide, ruleId, onEdit }) => {
  const [rule, setRule] = useState<Rule | null>(null);
  const [log, setLog] = useState<RuleEvaluationLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [reevaluating, setReevaluating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const eligibilityRef = useRef<EligibilityQueueHandle>(null);

  useEffect(() => {
    if (!show || !ruleId) return;
    setRule(null); setLog([]); setMsg(null); setErr(null);
    (async () => {
      try {
        const r: any = await RulesService.get(ruleId);
        setRule(r?.data?.data as Rule);
      } catch (e: any) {
        setErr(e?.response?.data?.error?.message || e?.message || "Failed to load rule");
      }
      loadLog();
    })();
  }, [show, ruleId]);

  const loadLog = async () => {
    if (!ruleId) return;
    setLogLoading(true);
    try {
      const l: any = await RulesService.log(ruleId, { limit: 50 });
      setLog((l?.data?.data || []) as RuleEvaluationLogEntry[]);
    } finally {
      setLogLoading(false);
    }
  };

  const reevaluateAll = async () => {
    if (!ruleId) return;
    if (!window.confirm("Re-evaluate this rule against ALL applicable enquiries? This enqueues work that the drain job will process over the next minute or two.")) return;
    setReevaluating(true);
    setMsg(null); setErr(null);
    try {
      const res: any = await RulesService.reevaluate(ruleId);
      setMsg(`Queued ${res?.data?.data?.enquiriesEnqueued || 0} evaluation(s). They will process within ~1 minute.`);
      setTimeout(loadLog, 2000);
    } catch (e: any) {
      setErr(e?.response?.data?.error?.message || e?.message || "Re-evaluate failed");
    } finally {
      setReevaluating(false);
    }
  };

  /** Derive "last fired" from the log so we don't need a dedicated endpoint. */
  const lastFired = useMemo(() => {
    return log.find((r) => r.result === "matched")?.createdDate || null;
  }, [log]);

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="ca-badge-modal ca-rules-modal rd-no-header">
      <Modal.Body>
        {/* HERO — replaces the redundant modal header. Carries the close affordance. */}
        {rule && (
          <div className="rd-hero">
            <div className="rd-hero-avatar">
              <Award size={26} />
            </div>
            <div className="rd-hero-main">
              <div className="rd-hero-name">{rule.name}</div>
              <div className="rd-hero-meta">
                <span className="rd-hero-badge-name">{rule.badgeName ?? "?"}</span>
                {rule.badgeAchievementType && (
                  <span className="rd-hero-pill rd-hero-pill-soft">{rule.badgeAchievementType}</span>
                )}
                <span className={`rd-hero-pill ${rule.enableStatus ? "is-enabled" : "is-disabled"}`}>
                  <span className="rd-status-dot" /> {rule.enableStatus ? "Enabled" : "Disabled"}
                </span>
                <span className={`rd-hero-pill rd-hero-mode ${rule.reviewRequired ? "is-review" : "is-auto"}`}>
                  {rule.reviewRequired
                    ? <><ShieldAlert size={11} /> Review-required · {(rule.reviewerUserIds || []).length} reviewer{(rule.reviewerUserIds || []).length === 1 ? "" : "s"}</>
                    : <><ShieldCheck size={11} /> Auto-issue</>}
                </span>
              </div>
            </div>
            <div className="rd-hero-actions">
              <Button size="sm" variant="primary" onClick={() => rule && onEdit(rule)}>Edit Rule</Button>
              <Button size="sm" variant="secondary" onClick={reevaluateAll} disabled={reevaluating}>
                <RefreshCw size={12} /> {reevaluating ? "Queueing…" : "Re-evaluate all"}
              </Button>
            </div>
            <button type="button" className="rd-close" onClick={onHide} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        )}

        {err && <div className="bm-error" style={{ marginTop: 12 }}>{err}</div>}
        {msg && <div className="rd-toast">{msg}</div>}

        {rule && (
          <>

            {/* PREDICATE + TRIGGERS */}
            <div className="rd-definition">
              <div className="rd-definition-row">
                <div className="rd-definition-label">Predicate</div>
                <code className="rd-definition-code">{predicateToString(rule.predicate)}</code>
              </div>
              <div className="rd-definition-row">
                <div className="rd-definition-label">Triggers</div>
                <div className="rd-trigger-chips">
                  {(rule.triggers?.events || []).length === 0
                    ? <span className="rd-trigger-empty">No triggers configured — rule will never fire</span>
                    : (rule.triggers?.events || []).map((e) => (
                        <span key={e} className="rd-trigger-chip">{e.replace(/_/g, " ")}</span>
                      ))}
                </div>
              </div>
            </div>

            {/* STATS STRIP */}
            <div className="rd-stats">
              <Stat
                icon={<Sparkles size={14} />}
                label="Fires in last 7d"
                value={rule.fires7d ?? 0}
                tone={(rule.fires7d ?? 0) > 0 ? "ok" : "muted"}
              />
              <Stat
                icon={<Users size={14} />}
                label="Pending review"
                value={rule.pendingEligibility ?? 0}
                tone={(rule.pendingEligibility ?? 0) > 0 ? "warn" : "muted"}
              />
              <Stat
                icon={<Trophy size={14} />}
                label="Last fired"
                value={lastFired ? relativeTime(lastFired) : "Never"}
                tone={lastFired ? "ok" : "muted"}
              />
            </div>

            {/* PENDING ELIGIBILITY */}
            {rule.reviewRequired && (
              <div className="rd-block">
                <div className="rd-block-head">
                  <h5>
                    <ShieldAlert size={13} /> Pending eligibility
                    {pendingCount !== null && pendingCount > 0 && (
                      <span className="rd-head-count">{pendingCount}</span>
                    )}
                  </h5>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => eligibilityRef.current?.refresh()}
                  >
                    <RefreshCw size={11} /> Refresh
                  </Button>
                </div>
                <EligibilityQueue
                  ref={eligibilityRef}
                  ruleId={rule.id}
                  embedded
                  onCountChange={setPendingCount}
                />
              </div>
            )}

            {/* RECENT EVALUATIONS */}
            <div className="rd-block">
              <div className="rd-block-head">
                <h5><Activity size={13} /> Recent evaluations</h5>
                <Button size="sm" variant="secondary" onClick={loadLog} disabled={logLoading}>
                  <RefreshCw size={11} /> {logLoading ? "Loading…" : "Refresh"}
                </Button>
              </div>

              {log.length === 0 && !logLoading ? (
                <EmptyState
                  icon={<Activity size={22} />}
                  title="No evaluations yet"
                  body="This rule hasn't been evaluated since it was created. It will start logging the moment one of its triggers fires — try Re-evaluate all to seed it now."
                />
              ) : (
                <div className="rd-log-table-wrap">
                  <table className="rd-log-table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Student</th>
                        <th>Trigger</th>
                        <th>Result</th>
                        <th>Credential</th>
                      </tr>
                    </thead>
                    <tbody>
                      {log.map((row) => (
                        <tr key={row.id}>
                          <td title={new Date(row.createdDate).toLocaleString()}>
                            {relativeTime(row.createdDate)}
                          </td>
                          <td>
                            <div className="rd-log-student">
                              {row.studentName || `Enquiry #${row.enquiryId}`}
                            </div>
                            {row.studentCode && (
                              <code className="rd-log-student-code">{row.studentCode}</code>
                            )}
                          </td>
                          <td><span className="rd-trigger-chip">{row.triggerType}</span></td>
                          <td>
                            <span className={`rd-result is-${row.result.replace("_","-")}`}>
                              {row.result === "matched" && <CheckCircle2 size={11} />}
                              {row.result.replace("_", " ")}
                            </span>
                          </td>
                          <td>
                            {row.issuedCredentialId
                              ? <span className="rd-issued">#{row.issuedCredentialId} <ExternalLink size={10} /></span>
                              : <span className="rd-cell-empty">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

const Stat: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone: "ok" | "warn" | "muted";
}> = ({ icon, label, value, tone }) => (
  <div className={`rd-stat is-${tone}`}>
    <span className="rd-stat-icon">{icon}</span>
    <div className="rd-stat-meta">
      <div className="rd-stat-label">{label}</div>
      <div className="rd-stat-value">{value}</div>
    </div>
  </div>
);

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  body: string;
}> = ({ icon, title, body }) => (
  <div className="rd-empty">
    <div className="rd-empty-icon">{icon}</div>
    <div className="rd-empty-title">{title}</div>
    <div className="rd-empty-body">{body}</div>
  </div>
);

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const diffMs = Date.now() - t;
  const s = Math.round(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default RuleDetail;
